import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useUsage } from '../hooks/useUsage';

const LABELS = {
  forms:                  { name: 'forms',                  storage: false },
  submissions_this_month: { name: 'submissions this month', storage: false },
  signed_docs_this_month: { name: 'signed documents',       storage: false },
  storage:                { name: 'storage',                storage: true  },
};

const fmtStorage = (bytes) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

// Banner shown on Dashboard when ANY quota is >= 80%.
// Enterprise users never see it (their limits are unlimited).
export const UsageNudgeBanner = () => {
  const { usage, isEnterprise, getNearLimitQuotas } = useUsage();
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  if (!usage || isEnterprise || dismissed) return null;

  const nearQuotas = getNearLimitQuotas();
  if (nearQuotas.length === 0) return null;

  // Pick the worst offender for headline
  const worst = nearQuotas
    .map((k) => ({ key: k, pct: usage.percentages?.[k] ?? 0 }))
    .sort((a, b) => b.pct - a.pct)[0];

  const isCritical = worst.pct >= 100;
  const worstLabel = LABELS[worst.key]?.name || worst.key;

  const currentVal = (() => {
    if (worst.key === 'forms') return `${usage.current.forms} / ${usage.limits.max_forms}`;
    if (worst.key === 'submissions_this_month') return `${usage.current.submissions_this_month} / ${usage.limits.max_submissions_per_month}`;
    if (worst.key === 'signed_docs_this_month') return `${usage.current.signed_docs_this_month} / ${usage.limits.max_signed_documents_per_month}`;
    if (worst.key === 'storage') return `${fmtStorage(usage.current.storage_bytes)} / ${fmtStorage(usage.limits.storage_bytes)}`;
    return '';
  })();

  const upgradeCta = usage.upgrade_to ? `Upgrade to ${usage.upgrade_to}` : 'Contact Sales';

  return (
    <div
      data-testid="usage-nudge-banner"
      className={`rounded-xl border-2 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
        isCritical
          ? 'bg-rose-50 border-rose-200 text-rose-900'
          : 'bg-amber-50 border-amber-200 text-amber-900'
      }`}
    >
      <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
        isCritical ? 'bg-rose-100' : 'bg-amber-100'
      }`}>
        <AlertTriangle className={`w-5 h-5 ${isCritical ? 'text-rose-600' : 'text-amber-600'}`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold leading-tight">
          {isCritical
            ? `You've hit your ${usage.plan_display_name} ${worstLabel} limit`
            : `You're at ${Math.round(worst.pct)}% of your ${worstLabel} limit`}
        </p>
        <p className="text-xs mt-0.5 opacity-80">
          {worstLabel.charAt(0).toUpperCase() + worstLabel.slice(1)}: <span className="font-mono font-semibold">{currentVal}</span>
          {nearQuotas.length > 1 && (
            <span className="ml-1">· +{nearQuotas.length - 1} more near limit</span>
          )}
        </p>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          onClick={() => {
            if (usage.upgrade_to) navigate('/billing');
            else window.location.assign('mailto:sales@formlio.app?subject=Formlio%20Enterprise%20Inquiry');
          }}
          data-testid="usage-nudge-upgrade-btn"
          className={`${
            isCritical ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'
          } text-white font-semibold`}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          {upgradeCta}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        <button
          onClick={() => setDismissed(true)}
          data-testid="usage-nudge-dismiss"
          className="w-8 h-8 rounded-md hover:bg-black/5 flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
